// AppContext.js
import { useMediaQuery, useTheme } from "@mui/material";
import React, { createContext, useContext, useState, useEffect } from "react";
import { getAssetUrl } from "../helpers/api_helper";

// Create the context
const AppContext = createContext();

// Create a custom hook to use the context
export const useAppContext = () => {
  const context = useContext(AppContext);
  if (!context) {
    throw new Error("useAppContext must be used within an AppProvider");
  }
  return context;
};

const useResponsive = (query) => {
  const [matches, setMatches] = useState(false);

  useEffect(() => {
    const media = window.matchMedia(query);
    if (media.matches !== matches) {
      setMatches(media.matches);
    }
    const listener = () => setMatches(media.matches);
    media.addEventListener("change", listener);
    return () => media.removeEventListener("change", listener);
  }, [matches, query]);

  return matches;
};

// Create the provider component
export const AppProvider = ({ children }) => {
  // Theme state
  const theme = useTheme();
  // const isMobile = useMediaQuery(theme.breakpoints.down('md'), { noSsr: true });
  // const isSmallMobile = useMediaQuery(theme.breakpoints.down('sm'), { noSsr: true });
  const isMobile = useResponsive("(max-width: 960px)");
  const isSmallMobile = useResponsive("(max-width: 600px)");
  const User = localStorage.getItem("User");

  // User data state
  const [userData, setUserData] = useState({
    fullName: "",
    User_Photo_Path: "",
    isLoggedIn: false,
  });

  // Sidebar states
  const [drawerOpen, setDrawerOpen] = useState(false);
  const [profileMenuAnchor, setProfileMenuAnchor] = useState(null);
  const [clickedNewEvaluation, setClickedNewEvaluation] = useState(false);

  // Unsaved changes guard states
  const [unsavedChanges, setUnsavedChanges] = useState(false);
  const [pendingNavigation, setPendingNavigation] = useState(null);

  useEffect(() => {
    if (User) {
      const user_ = JSON.parse(User);
      setUserData({
        ...userData,
        fullName: user_?.UserInfo?.fullName,
        User_Photo_Path: getAssetUrl(user_?.UserInfo?.User_Photo_Path),
        isLoggedIn: true
      });
    }
  }, [User]);

  // ------------ FUNCTIONS ----------
  // User logout function
  const logout = () => {
    setUserData({
      fullName: "",
      User_Photo_Path: "",
      isLoggedIn: false,
    });
  };

  // Update user data
  const updateUserData = (newData) => {
    setUserData({
      ...userData,
      ...newData,
    });
  };

  const registerUnsavedChanges = (value) => {
    setUnsavedChanges(!!value);
  };

  const guardedNavigate = (navigateFn, path) => {
    if (unsavedChanges) {
      setPendingNavigation({ navigateFn, path });
    } else {
      if (typeof navigateFn === "function") {
        navigateFn(path);
      }
    }
  };

  const confirmLeave = () => {
    if (pendingNavigation) {
      const { navigateFn, path } = pendingNavigation;
      if (typeof navigateFn === "function") {
        navigateFn(path);
      }
    }
    setUnsavedChanges(false);
    setPendingNavigation(null);
  };

  const cancelLeave = () => {
    setPendingNavigation(null);
  };

  const value = {
    // Theme
    isMobile,
    isSmallMobile,

    // User data
    userData,
    logout,
    updateUserData,
    drawerOpen,
    setDrawerOpen,
    clickedNewEvaluation,
    setClickedNewEvaluation,
    profileMenuAnchor,
    setProfileMenuAnchor,

    // Unsaved changes guard
    unsavedChanges,
    pendingNavigation,
    registerUnsavedChanges,
    guardedNavigate,
    confirmLeave,
    cancelLeave,
  };

  return <AppContext.Provider value={value}>{children}</AppContext.Provider>;
};
